const DEFAULT_SETTINGS = {
  image_model: "",
  available_models: [],
  model_options: [],
  chat_model: "",
  chat_available_models: [],
  chat_model_options: [],
  frontend_title: "Image Pro",
  frontend_logo_svg: null,
  frontend_logo_url: "/assets/img/logo.png",
  frontend_login_background_url: null,
  frontend_chat_enabled: true,
  allow_user_registration: true,
  enable_registration_bonus: false,
  registration_bonus_credits: 0,
  allow_guest_generation: true,
  guest_daily_limit: 3,
  enable_ad_overlay: false,
  ad_sponsored_label_en: "Sponsored",
  ad_sponsored_label_zh: "赞助",
  ad_overlay_items: [],
};

export function normalizeTitle(value) {
  const title = String(value || "").trim();
  return title || DEFAULT_SETTINGS.frontend_title;
}

export function normalizeSettings(rawSettings) {
  const settings = rawSettings && typeof rawSettings === "object" ? rawSettings : {};
  return {
    ...DEFAULT_SETTINGS,
    ...settings,
    frontend_title: normalizeTitle(settings.frontend_title),
    frontend_logo_url: settings.frontend_logo_url || DEFAULT_SETTINGS.frontend_logo_url,
    available_models: Array.isArray(settings.available_models)
      ? settings.available_models.filter(Boolean)
      : DEFAULT_SETTINGS.available_models,
    model_options: Array.isArray(settings.model_options) ? settings.model_options : [],
    chat_available_models: Array.isArray(settings.chat_available_models)
      ? settings.chat_available_models.filter(Boolean)
      : [],
    chat_model_options: Array.isArray(settings.chat_model_options) ? settings.chat_model_options : [],
    ad_overlay_items: Array.isArray(settings.ad_overlay_items) ? settings.ad_overlay_items : [],
  };
}

function modelsFromPublicList(options, allowedIds) {
  const allowed = new Set((Array.isArray(allowedIds) ? allowedIds : []).map((item) => String(item || "").trim()).filter(Boolean));
  const seen = new Set();
  const fromOptions = (Array.isArray(options) ? options : [])
    .filter((item) => item?.enabled !== false)
    .map((item) => ({
      id: String(item?.id || "").trim(),
      label: String(item?.display_name || "").trim(),
    }))
    .filter((item) => item.id && (!allowed.size || allowed.has(item.id)))
    .filter((item) => {
      if (seen.has(item.id)) {
        return false;
      }
      seen.add(item.id);
      return true;
    })
    .map((item) => ({
      id: item.id,
      label: item.label || item.id,
    }));
  if (fromOptions.length) {
    return fromOptions;
  }
  return Array.from(allowed).map((id) => ({ id, label: id }));
}

export function normalizeModels(settings) {
  const safeSettings = settings && typeof settings === "object" ? settings : {};
  return modelsFromPublicList(safeSettings.model_options, safeSettings.available_models);
}

export function normalizeChatModels(settings) {
  const safeSettings = settings && typeof settings === "object" ? settings : {};
  return modelsFromPublicList(safeSettings.chat_model_options, safeSettings.chat_available_models);
}

export async function fetchPublicSettings() {
  try {
    const response = await fetch("/api/public/settings", { cache: "no-store" });
    if (!response.ok) {
      throw new Error(`settings request failed: ${response.status}`);
    }
    return normalizeSettings(await response.json());
  } catch (error) {
    console.warn("Using default frontend settings", error);
    return normalizeSettings(DEFAULT_SETTINGS);
  }
}

const AUTH_STATUS_STORAGE_KEY = "image-pro:auth-status";

export function readStoredAuthStatus() {
  if (typeof window === "undefined") {
    return null;
  }
  try {
    const raw = window.localStorage.getItem(AUTH_STATUS_STORAGE_KEY);
    if (!raw) {
      return null;
    }
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object" || parsed.authenticated !== true || !parsed.user?.id) {
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}

export function writeStoredAuthStatus(authInfo) {
  if (typeof window === "undefined") {
    return;
  }
  try {
    if (authInfo?.authenticated && authInfo.user?.id) {
      window.localStorage.setItem(AUTH_STATUS_STORAGE_KEY, JSON.stringify({
        authenticated: true,
        user: authInfo.user,
        registration: authInfo.registration || null,
        guest: authInfo.guest || null,
      }));
      return;
    }
    window.localStorage.removeItem(AUTH_STATUS_STORAGE_KEY);
  } catch {
    // Ignore quota or private-mode failures.
  }
}

export async function fetchAuthStatus() {
  const response = await fetch("/api/auth/me", {
    cache: "no-store",
    credentials: "same-origin",
  });
  const body = await response.json().catch(() => ({}));
  if (!response.ok) {
    const error = new Error(extractErrorMessage(body, response.status) || `HTTP ${response.status}`);
    error.status = response.status;
    throw error;
  }
  writeStoredAuthStatus(body);
  return body;
}

async function requestAccountJson(url, init = {}) {
  const response = await fetch(url, {
    credentials: "same-origin",
    ...init,
  });
  const body = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error(extractErrorMessage(body, response.status) || `HTTP ${response.status}`);
  }
  return body;
}

export async function loginUser(username, password, remember = false) {
  const response = await fetch("/api/auth/login", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    credentials: "same-origin",
    body: JSON.stringify({ username, password, remember: Boolean(remember) }),
  });
  const body = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error(extractErrorMessage(body) || "登录失败");
  }
  writeStoredAuthStatus(body);
  return body;
}

export async function registerUser(username, email, password) {
  const response = await fetch("/api/auth/register", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    credentials: "same-origin",
    body: JSON.stringify({ username, email, password }),
  });
  const body = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error(extractErrorMessage(body) || "注册失败");
  }
  writeStoredAuthStatus(body);
  return body;
}

export async function logoutUser() {
  const response = await fetch("/api/auth/logout", {
    method: "POST",
    cache: "no-store",
    credentials: "same-origin",
  });
  const body = await response.json().catch(() => ({}));
  writeStoredAuthStatus(null);
  if (!response.ok) {
    throw new Error(extractErrorMessage(body) || "退出登录失败");
  }
  return body;
}

export async function redeemCode(code) {
  const response = await fetch("/api/auth/redeem", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    credentials: "same-origin",
    body: JSON.stringify({ code }),
  });
  const body = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error(extractErrorMessage(body) || "兑换失败");
  }
  return body;
}

export async function fetchAccountNotifications(options = {}) {
  const params = new URLSearchParams();
  if (options.type) {
    params.set("type", String(options.type));
  }
  if (options.unread !== undefined && options.unread !== null) {
    params.set("unread", options.unread ? "true" : "false");
  }
  if (options.limit !== undefined && options.limit !== null) {
    params.set("limit", String(options.limit));
  }
  const query = params.toString();
  return requestAccountJson(query ? `/api/account/notifications?${query}` : "/api/account/notifications", {
    cache: "no-store",
  });
}

export async function markAccountNotificationRead(notificationIds) {
  return requestAccountJson("/api/account/notifications/read", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      notification_ids: Array.isArray(notificationIds) ? notificationIds : [notificationIds],
    }),
  });
}

export async function markAccountNotificationsReadAll() {
  return requestAccountJson("/api/account/notifications/read-all", {
    method: "POST",
  });
}

export async function deleteAccountNotification(notificationId) {
  return requestAccountJson(`/api/account/notifications/${encodeURIComponent(notificationId)}`, {
    method: "DELETE",
  });
}

export async function bulkDeleteAccountNotifications(notificationIds) {
  return requestAccountJson("/api/account/notifications/bulk-delete", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      notification_ids: Array.isArray(notificationIds) ? notificationIds : [],
    }),
  });
}

export async function fetchAccountCreditsLedger(limit = 50) {
  return requestAccountJson(`/api/account/credits/ledger?limit=${encodeURIComponent(limit)}`, {
    cache: "no-store",
  });
}

export async function updateAccountPassword(currentPassword, newPassword) {
  return requestAccountJson("/api/account/password", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      current_password: currentPassword,
      new_password: newPassword,
    }),
  });
}

export async function updateAccountEmail(currentPassword, email) {
  return requestAccountJson("/api/account/email", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      current_password: currentPassword,
      email,
    }),
  });
}

export async function updateAccountPhone(currentPassword, phone) {
  return requestAccountJson("/api/account/phone", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      current_password: currentPassword,
      phone,
    }),
  });
}

export async function fetchGenerationHistory(limit = 16) {
  const response = await fetch(`/api/generation/history?limit=${encodeURIComponent(limit)}`, {
    cache: "no-store",
    credentials: "same-origin",
  });
  const body = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error(extractErrorMessage(body, response.status) || `HTTP ${response.status}`);
  }
  return body;
}

export async function fetchGenerationTaskResult(taskId) {
  const response = await fetch(`/api/task/${encodeURIComponent(taskId)}`, {
    cache: "no-store",
    credentials: "same-origin",
  });
  const body = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error(extractErrorMessage(body, response.status) || `HTTP ${response.status}`);
  }
  if (body.status !== "completed" || !body.result) {
    return null;
  }
  const textResult = normalizeTextGenerationResult(body.result, body.taskId || taskId);
  return textResult || normalizeImageGenerationResult(body.result, body.taskId || taskId);
}

export async function deleteGenerationHistoryItem(taskId) {
  const response = await fetch(`/api/generation/history/${encodeURIComponent(taskId)}`, {
    method: "DELETE",
    credentials: "same-origin",
  });
  const body = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error(extractErrorMessage(body, response.status) || `HTTP ${response.status}`);
  }
  return body;
}

export async function startGeneration(payload) {
  const response = await fetch("/api/generate", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    credentials: "same-origin",
    body: JSON.stringify(payload),
  });
  const body = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error(extractErrorMessage(body, response.status) || `HTTP ${response.status}`);
  }
  if (!body.taskId) {
    throw new Error("生成任务没有返回 taskId");
  }
  return body.taskId;
}

export async function generateImageStream(payload, options = {}) {
  if (typeof ReadableStream === "undefined") {
    const taskId = await startGeneration(payload);
    return pollTask(taskId, options);
  }
  const response = await fetch("/api/generate/stream", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    credentials: "same-origin",
    body: JSON.stringify(payload),
    signal: options.signal,
  });
  if (!response.ok) {
    const body = await response.json().catch(() => ({}));
    throw new Error(extractErrorMessage(body, response.status) || `HTTP ${response.status}`);
  }
  if (!response.body?.getReader) {
    const text = await response.text();
    let bufferedResult = null;
    let bufferedTaskId = "";
    for (const line of text.split("\n")) {
      const event = parseStreamLine(line);
      if (!event) {
        continue;
      }
      if (event.taskId) {
        bufferedTaskId = event.taskId;
      }
      if (event.event === "result") {
        bufferedResult = normalizeGenerationResult(event);
      } else if (event.event === "error") {
        throw new Error(extractErrorMessage(event.error, Number(event.status || event.error?.status || 0)) || "生成失败");
      }
    }
    if (bufferedResult) {
      return bufferedResult;
    }
    if (bufferedTaskId) {
      return pollTask(bufferedTaskId, { ...options, skipInitialDelay: true });
    }
    throw new Error("生成任务没有返回 taskId");
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";
  let result = null;
  let streamTaskId = "";

  try {
    while (true) {
      let chunk;
      try {
        chunk = await reader.read();
      } catch (error) {
        if (error?.name === "AbortError") {
          throw error;
        }
        if (streamTaskId) {
          break;
        }
        throw error;
      }
      const { value, done } = chunk;
      buffer += decoder.decode(value || new Uint8Array(), { stream: !done });
      const lines = buffer.split("\n");
      buffer = lines.pop() || "";

      for (const line of lines) {
        const event = parseStreamLine(line);
        if (!event) {
          continue;
        }
        if (event.taskId) {
          streamTaskId = event.taskId;
        }
        if (event.event === "status") {
          options.onStatus?.(event.status || "pending");
        } else if (event.event === "result") {
          result = normalizeGenerationResult(event);
          options.onResult?.(result);
        } else if (event.event === "cache") {
          options.onCache?.(normalizeCacheResult(event));
        } else if (event.event === "error") {
          throw new Error(extractErrorMessage(event.error, Number(event.status || event.error?.status || 0)) || "生成失败");
        }
      }

      if (done) {
        break;
      }
    }
  } finally {
    reader.releaseLock();
  }

  if (!result && streamTaskId) {
    options.onStatus?.("waiting");
    return pollTask(streamTaskId, { ...options, skipInitialDelay: true });
  }

  if (!result) {
    throw new Error("生成完成但没有返回图片地址");
  }
  return result;
}

export async function sendChatCompletion(payload, options = {}) {
  const requestPayload = options.preferStream ? { ...payload, stream: true } : payload;
  const response = await fetch("/api/chat/completions", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    credentials: "same-origin",
    body: JSON.stringify(requestPayload),
    signal: options.signal,
  });
  const contentType = response.headers.get("content-type") || "";
  if (!response.ok) {
    const body = await response.json().catch(() => ({}));
    const errorKey = extractErrorKey(body);
    const message = extractChatErrorMessage(body, response.status);
    const rawMessage = extractRawErrorMessage(body);
    if (
      options.preferStream &&
      response.status === 400 &&
      (errorKey === "errors.chat.stream_not_supported" || /Console API returned 400/i.test(rawMessage))
    ) {
      return sendChatCompletion(payload, { ...options, preferStream: false });
    }
    throw new Error(message);
  }
  const canReadStream = Boolean(response.body?.getReader);
  const isJson = contentType.includes("application/json");
  if (options.onDelta && canReadStream && !isJson) {
    return readChatCompletionStream(response, options);
  }
  const body = await response.json().catch(() => ({}));
  return body;
}

export async function uploadChatAttachment(file, options = {}) {
  const formData = new FormData();
  formData.append("file", file);
  const response = await fetch("/api/chat/uploads", {
    method: "POST",
    credentials: "same-origin",
    body: formData,
    signal: options.signal,
  });
  const body = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error(extractChatErrorMessage(body, response.status));
  }
  return {
    id: String(body.id || ""),
    name: String(body.name || file?.name || "attachment"),
    size: Number(body.size || file?.size || 0),
    mimeType: String(body.mimeType || file?.type || ""),
    kind: body.kind === "image" ? "image" : "file",
    textContent: String(body.textContent || ""),
    textTruncated: Boolean(body.textTruncated),
    url: String(body.url || ""),
  };
}

function normalizeChatArtifacts(value) {
  if (!Array.isArray(value)) {
    return [];
  }
  return value.map((item) => {
    const artifact = item && typeof item === "object" ? item : {};
    const mimeType = String(artifact.mimeType || artifact.mime_type || "");
    return {
      id: String(artifact.id || artifact.url || artifact.name || ""),
      name: String(artifact.name || "artifact"),
      size: Number(artifact.size || 0),
      mimeType,
      mime_type: mimeType,
      kind: artifact.kind === "image" || mimeType.startsWith("image/") ? "image" : "file",
      url: String(artifact.url || ""),
      source: String(artifact.source || ""),
    };
  }).filter((item) => item.url);
}

async function readChatCompletionStream(response, options = {}) {
  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";
  let message = "";
  let reasoning = "";
  let artifacts = [];
  let finalPayload = null;
  let pendingSseEventName = "";

  function handleChatStreamEvent(event) {
    if (!event) {
      return;
    }
    const eventName = getChatStreamEventName(event);
    if (eventName === "retry") {
      options.onRetry?.(event);
      return;
    }
    const errorMessage = extractChatStreamErrorMessage(event);
    if (errorMessage) {
      throw new Error(errorMessage);
    }
    if (event.done) {
      return;
    }
    if (eventName === "done") {
      finalPayload = event;
      const finalReasoning = extractChatFinalReasoning(event);
      if (finalReasoning) {
        reasoning = finalReasoning;
      }
      artifacts = normalizeChatArtifacts(event.artifacts);
      return;
    }
    const reasoningDelta = extractChatStreamReasoningDelta(event);
    if (reasoningDelta) {
      reasoning += reasoningDelta;
      options.onReasoningDelta?.(reasoningDelta, event);
    }
    const delta = extractChatStreamDelta(event);
    if (delta) {
      message += delta;
      options.onDelta?.(delta, event);
    }
    if (typeof event.message === "string") {
      finalPayload = event;
      const finalReasoning = extractChatFinalReasoning(event);
      if (finalReasoning) {
        reasoning = finalReasoning;
      }
      const eventArtifacts = normalizeChatArtifacts(event.artifacts);
      if (eventArtifacts.length) {
        artifacts = eventArtifacts;
      }
    }
  }

  try {
    while (true) {
      const { value, done } = await reader.read();
      buffer += decoder.decode(value || new Uint8Array(), { stream: !done });
      const lines = buffer.split("\n");
      buffer = lines.pop() || "";
      for (const line of lines) {
        const event = parseChatStreamLine(line, pendingSseEventName);
        if (!event) {
          const nextEventName = parseChatSseEventName(line);
          if (nextEventName) {
            pendingSseEventName = nextEventName;
          }
          continue;
        }
        pendingSseEventName = "";
        handleChatStreamEvent(event);
      }
      if (done) {
        break;
      }
    }
  } finally {
    reader.releaseLock();
  }

  if (buffer.trim()) {
    const event = parseChatStreamLine(buffer, pendingSseEventName);
    handleChatStreamEvent(event);
  }

  const finalReasoning = extractChatFinalReasoning(finalPayload) || reasoning;
  const finalArtifacts = normalizeChatArtifacts(finalPayload?.artifacts);
  return {
    message: finalPayload?.message || message,
    reasoning: finalReasoning,
    reasoningContent: finalReasoning,
    reasoning_content: finalReasoning,
    artifacts: finalArtifacts.length ? finalArtifacts : artifacts,
    model: finalPayload?.model || "",
    providerId: finalPayload?.providerId || finalPayload?.provider_id || "",
    providerLabel: finalPayload?.providerLabel || finalPayload?.provider_label || "",
    raw: finalPayload || {},
  };
}

export async function pollTask(taskId, options = {}) {
  const intervalMs = options.intervalMs ?? 1000;
  const initialDelayMs = options.initialDelayMs ?? 0;
  const timeoutMs = options.timeoutMs ?? 1200000;
  const maxNetworkRetries = options.maxNetworkRetries ?? 3;
  const maxMissingTaskRetries = options.maxMissingTaskRetries ?? 5;
  const startedAt = Date.now();
  let effectiveDeadline = startedAt + timeoutMs;
  let networkRetries = 0;
  let missingTaskRetries = 0;

  if (!options.skipInitialDelay) {
    await delay(initialDelayMs, options.signal);
  }

  while (Date.now() < effectiveDeadline) {
    let response;
    try {
      response = await fetch(`/api/task/${encodeURIComponent(taskId)}`, {
        signal: options.signal,
        cache: "no-store",
        credentials: "same-origin",
      });
    } catch (error) {
      if (error?.name === "AbortError") {
        throw error;
      }
      if (error instanceof TypeError && networkRetries < maxNetworkRetries) {
        networkRetries += 1;
        options.onRetry?.(networkRetries, maxNetworkRetries);
        await delay(intervalMs, options.signal);
        continue;
      }
      throw new Error("网络错误，请检查网络连接后重试。");
    }

    networkRetries = 0;
    const body = await response.json().catch(() => ({}));
    if (!response.ok) {
      if (response.status === 404 && missingTaskRetries < maxMissingTaskRetries) {
        missingTaskRetries += 1;
        options.onStatus?.("waiting");
        await delay(intervalMs, options.signal);
        continue;
      }
      throw new Error(extractErrorMessage(body, response.status) || `任务查询失败：${response.status}`);
    }

    missingTaskRetries = 0;
    const taskDeadline = Date.parse(body.deadlineAt || body.deadline_at || "");
    if (Number.isFinite(taskDeadline)) {
      effectiveDeadline = Math.max(effectiveDeadline, taskDeadline + 60000);
    }

    options.onStatus?.(body.status || "pending");

    if (body.status === "completed" && body.result) {
      const textResult = normalizeTextGenerationResult(body.result, body.taskId || taskId);
      if (textResult) {
        return textResult;
      }
      return normalizeImageGenerationResult(body.result, body.taskId || taskId);
    }
    if (["failed", "timeout", "cancelled"].includes(body.status)) {
      throw new Error(extractErrorMessage(body.error, response.status) || "生成失败");
    }

    await delay(intervalMs, options.signal);
  }

  throw new Error("任务轮询超时");
}

function parseStreamLine(line) {
  const text = String(line || "").trim();
  if (!text) {
    return null;
  }
  try {
    return JSON.parse(text);
  } catch {
    return null;
  }
}

function parseChatSseEventName(line) {
  const text = String(line || "").trim();
  if (!/^event:/i.test(text)) {
    return "";
  }
  return text.slice(6).trim();
}

function parseChatStreamLine(line, eventName = "") {
  const text = String(line || "").trim();
  if (!text) {
    return null;
  }
  if (/^(event|id|retry):/i.test(text) || text.startsWith(":")) {
    return null;
  }
  const payload = text.startsWith("data:") ? text.slice(5).trim() : text;
  if (!payload || payload === "[DONE]") {
    return { done: true };
  }
  try {
    const event = JSON.parse(payload);
    if (event && typeof event === "object" && eventName && !event.event && !event.type) {
      return { event: eventName, ...event };
    }
    return event;
  } catch {
    return eventName ? { event: eventName, delta: payload } : { delta: payload };
  }
}

function getChatStreamEventName(event) {
  return String(event?.event || event?.type || "").trim().toLowerCase();
}

function extractChatStreamErrorMessage(event) {
  if (!event || event.done) {
    return "";
  }
  const eventName = getChatStreamEventName(event);
  if (eventName === "retry") {
    return "";
  }
  if (eventName !== "error" && event.status !== "failed" && !event.error) {
    return "";
  }
  return extractChatErrorMessage(event, extractErrorStatus(event, Number(event.status || 0)));
}

function extractChatStreamDelta(event) {
  if (!event || event.done) {
    return "";
  }
  const eventName = getChatStreamEventName(event);
  if (eventName && eventName !== "delta") {
    return "";
  }
  if (typeof event.delta === "string") {
    return event.delta;
  }
  if (typeof event.content === "string") {
    return event.content;
  }
  const choices = Array.isArray(event.choices) ? event.choices : [];
  const first = choices[0] && typeof choices[0] === "object" ? choices[0] : {};
  const delta = first.delta && typeof first.delta === "object" ? first.delta : {};
  if (typeof delta.content === "string") {
    return delta.content;
  }
  const message = first.message && typeof first.message === "object" ? first.message : {};
  return typeof message.content === "string" ? message.content : "";
}

function extractChatReasoningText(value) {
  if (typeof value === "string") {
    return value;
  }
  if (Array.isArray(value)) {
    return value.map((item) => extractChatReasoningText(item)).filter(Boolean).join("");
  }
  if (value && typeof value === "object") {
    for (const key of ["text", "content", "summary", "reasoning", "reasoning_content", "reasoningContent"]) {
      const text = extractChatReasoningText(value[key]);
      if (text) {
        return text;
      }
    }
  }
  return "";
}

function extractChatStreamReasoningDelta(event) {
  if (!event || event.done) {
    return "";
  }
  const eventName = getChatStreamEventName(event);
  if (eventName === "done" || eventName === "retry" || eventName === "error") {
    return "";
  }
  if (eventName === "reasoning") {
    for (const key of ["delta", "reasoningDelta", "reasoning_delta", "content"]) {
      const text = extractChatReasoningText(event[key]);
      if (text) {
        return text;
      }
    }
  }
  if (eventName && eventName !== "delta") {
    return "";
  }
  for (const key of ["reasoningDelta", "reasoning_delta", "reasoning_content", "reasoningContent", "reasoning"]) {
    const text = extractChatReasoningText(event[key]);
    if (text) {
      return text;
    }
  }
  const choices = Array.isArray(event.choices) ? event.choices : [];
  const first = choices[0] && typeof choices[0] === "object" ? choices[0] : {};
  const delta = first.delta && typeof first.delta === "object" ? first.delta : {};
  const message = first.message && typeof first.message === "object" ? first.message : {};
  for (const source of [delta, message, first]) {
    for (const key of ["reasoning_content", "reasoningContent", "reasoning"]) {
      const text = extractChatReasoningText(source[key]);
      if (text) {
        return text;
      }
    }
  }
  return "";
}

function extractChatFinalReasoning(payload) {
  if (!payload || typeof payload !== "object") {
    return "";
  }
  for (const key of ["reasoning", "reasoningContent", "reasoning_content"]) {
    const text = extractChatReasoningText(payload[key]);
    if (text) {
      return text;
    }
  }
  return extractChatStreamReasoningDelta(payload);
}

function thumbnailUrlFromLocalAsset(src) {
  const text = String(src || "").trim();
  const prefix = "/generated/";
  try {
    const url = new URL(text, typeof window === "undefined" ? "http://localhost" : window.location.href);
    if (url.pathname.startsWith(prefix) && (typeof window === "undefined" || url.origin === window.location.origin)) {
      return `/generated-thumbnails/${url.pathname.slice(prefix.length)}`;
    }
  } catch {
    if (text.startsWith(prefix)) {
      return `/generated-thumbnails/${text.slice(prefix.length)}`;
    }
  }
  return "";
}

function normalizeGenerationResult(event) {
  const body = event.result || {};
  const textResult = normalizeTextGenerationResult(body, event.taskId || "");
  if (textResult) {
    return textResult;
  }
  return normalizeImageGenerationResult(body, event.taskId || "");
}

function normalizeImageGenerationResult(body, taskId = "") {
  const images = normalizeGeneratedImages(body);
  const primary = images[0] || {};
  const displayUrl = primary.displayUrl || primary.display_url || primary.imageUrl || primary.image_url;
  if (!displayUrl) {
    throw new Error("生成完成但没有返回图片地址");
  }
  return {
    taskId,
    imageUrl: primary.imageUrl || primary.image_url || displayUrl,
    displayUrl,
    cachedImageUrl: primary.cachedImageUrl || primary.cached_image_url || "",
    thumbnailUrl: primary.thumbnailUrl || primary.thumbnail_url || thumbnailUrlFromLocalAsset(primary.cachedImageUrl || primary.cached_image_url || displayUrl),
    images,
    raw: body,
  };
}

function normalizeGeneratedImages(body) {
  const source = body && typeof body === "object" ? body : {};
  const rawImages = Array.isArray(source.images) ? source.images : [];
  const images = rawImages.map(normalizeGeneratedImage).filter(Boolean);
  const primary = normalizeGeneratedImage(source);
  if (primary) {
    const primaryKey = primary.cachedImageUrl || primary.cached_image_url || primary.imageUrl || primary.image_url || primary.displayUrl || primary.display_url;
    const exists = images.some((image) => (
      (image.cachedImageUrl || image.cached_image_url || image.imageUrl || image.image_url || image.displayUrl || image.display_url) === primaryKey
    ));
    if (!exists) {
      images.unshift(primary);
    }
  }
  return images;
}

function normalizeGeneratedImage(image) {
  if (typeof image === "string") {
    return {
      imageUrl: image,
      image_url: image,
      displayUrl: image,
      display_url: image,
      cachedImageUrl: "",
      cached_image_url: "",
    };
  }
  if (!image || typeof image !== "object") {
    return null;
  }
  const displayUrl = image.displayUrl || image.display_url || image.url || image.imageUrl || image.image_url || image.cachedImageUrl || image.cached_image_url || "";
  if (!displayUrl) {
    return null;
  }
  const cachedImageUrl = image.cachedImageUrl || image.cached_image_url || "";
  return {
    ...image,
    imageUrl: image.imageUrl || image.image_url || image.url || displayUrl,
    image_url: image.image_url || image.imageUrl || image.url || displayUrl,
    displayUrl,
    display_url: image.display_url || image.displayUrl || displayUrl,
    cachedImageUrl,
    cached_image_url: image.cached_image_url || image.cachedImageUrl || "",
    thumbnailUrl: image.thumbnailUrl || image.thumbnail_url || thumbnailUrlFromLocalAsset(cachedImageUrl || displayUrl),
    thumbnail_url: image.thumbnail_url || image.thumbnailUrl || thumbnailUrlFromLocalAsset(cachedImageUrl || displayUrl),
  };
}

function normalizeTextGenerationResult(body, taskId = "") {
  const source = body && typeof body === "object" ? body : {};
  const resultType = String(source.type || source.resultType || source.result_type || "").trim();
  if (resultType !== "text_rejection") {
    return null;
  }
  const text = String(source.rejectionText || source.rejection_text || source.text || source.message || "").trim();
  if (!text) {
    return null;
  }
  return {
    taskId,
    type: "text_rejection",
    resultType: "text_rejection",
    result_type: "text_rejection",
    finishReason: source.finishReason || source.finish_reason || "content_policy",
    finish_reason: source.finish_reason || source.finishReason || "content_policy",
    rejectionText: text,
    rejection_text: text,
    text,
    message: text,
    raw: source,
  };
}

function normalizeCacheResult(event) {
  const body = event.result || {};
  return {
    taskId: event.taskId || "",
    cachedImageUrl: body.cachedImageUrl || body.cached_image_url || "",
    raw: body,
  };
}

function extractErrorMessageLegacy(payload) {
  if (!payload) {
    return "";
  }
  if (typeof payload === "string") {
    return payload;
  }
  if (typeof payload.message === "string") {
    return payload.message;
  }
  if (typeof payload.error === "string") {
    return payload.error;
  }
  if (payload.error && typeof payload.error.message === "string") {
    return payload.error.message;
  }
  if (payload.error && typeof payload.error.key === "string") {
    return payload.error.key;
  }
  return "";
}

const CHAT_ERROR_MESSAGES = {
  auth_required: "请先登录，或启用游客使用后再发送 Chat 消息。",
  insufficient_credits: "账户积分不足，请充值或切换低消耗模型后重试。",
  "errors.chat.model_not_available": "当前 Chat 模型不可用，请切换模型后重试。",
  "errors.chat.no_available_model": "当前没有可用的 Chat 模型，请先在后台启用文本或多模态模型。",
  "errors.chat.no_provider_for_model": "当前 Chat 模型没有可用供应商，请检查模型绑定的上游渠道。",
  "errors.chat.stream_not_supported": "当前服务暂不支持 Chat 流式响应，已自动切换为普通响应。",
  "errors.request.empty_file": "上传文件为空，请重新选择文件。",
  "errors.request.file_too_large": "上传文件超过 10 MB，请压缩后再试。",
  "errors.request.missing_file": "没有收到上传文件，请重新选择后上传。",
  "errors.request.missing_required_fields": "请输入消息内容或先上传可用附件。",
  "errors.request.unsupported_file_type": "当前仅支持 JPG、PNG、WEBP、GIF 图片，以及 TXT、MD、CSV、JSON、PDF 附件。",
};

function extractErrorKey(payload) {
  if (!payload) {
    return "";
  }
  if (typeof payload === "string") {
    return payload;
  }
  if (typeof payload.error === "string") {
    return payload.error;
  }
  if (payload.error && typeof payload.error.key === "string") {
    return payload.error.key;
  }
  if (typeof payload.message === "string" && /^(auth_required|errors\.)/.test(payload.message)) {
    return payload.message;
  }
  if (payload.error && typeof payload.error.message === "string" && /^(auth_required|errors\.)/.test(payload.error.message)) {
    return payload.error.message;
  }
  return "";
}

function extractChatErrorMessageLegacy(payload, status = 0) {
  const rawMessage = extractErrorMessage(payload) || extractErrorKey(payload) || "";
  if (CHAT_ERROR_MESSAGES[rawMessage]) {
    return CHAT_ERROR_MESSAGES[rawMessage];
  }
  if (/Console API returned 400/i.test(rawMessage)) {
    return "上游 Console API 拒绝了当前 Chat 消息格式，请重新上传附件，或切换到支持图片输入的 Chat 模型后重试。";
  }
  return rawMessage || `HTTP ${status}`;
}

const PUBLIC_ERROR_MESSAGES = {
  auth_required: "请先登录后再试。",
  insufficient_credits: "账户积分不足，请充值后再试。",
  guest_generation_disabled: "当前未开启游客生图，请登录后继续。",
  guest_quota_exceeded: "游客额度已用尽，请登录后继续。",
  "errors.chat.model_not_available": "当前 Chat 模型不可用，请切换模型后重试。",
  "errors.chat.no_available_model": "当前没有可用的 Chat 模型，请先在后台启用可用模型。",
  "errors.chat.no_provider_for_model": "当前 Chat 模型没有可用供应商，请检查模型绑定的上游渠道。",
  "errors.chat.stream_not_supported": "当前服务暂不支持 Chat 流式响应，已自动切换为普通响应。",
  "errors.request.empty_file": "上传文件为空，请重新选择文件。",
  "errors.request.file_too_large": "上传文件超过 10 MB，请压缩后再试。",
  "errors.request.missing_file": "没有收到上传文件，请重新选择后上传。",
  "errors.request.missing_required_fields": "请输入消息内容或先上传可用附件。",
  "errors.request.unsupported_file_type": "当前仅支持 JPG、PNG、WEBP、GIF 图片，以及 TXT、MD、CSV、JSON、PDF 附件。",
  "errors.server.configuration_error": "服务配置异常，请联系管理员。",
  "errors.server.queue_full": "服务繁忙，请稍后重试。",
  "errors.server.queue_unavailable": "当前服务暂不可用，请稍后重试。",
  "errors.server.generation_timeout": "生成超时，请稍后重试。",
  "errors.server.process_request_failed": "请求处理失败，请稍后重试。",
  "errors.task.not_found_or_expired": "任务已过期或不存在，请重新提交。",
  "errors.api.invalid_response": "上游返回异常，请稍后重试。",
  "errors.api.empty_response": "上游没有返回任何可显示的回复，请稍后重试。",
  "errors.api.internal_server_error": "上游返回异常，请稍后重试。",
  "errors.api.no_image_data": "上游没有返回图片数据。",
  "errors.api.no_image_generated": "生成失败，服务端没有返回图片。",
  "errors.image.download_failed": "图片下载失败，请稍后重试。",
  "errors.upstream.connection_failed": "HTTP 502",
  "errors.upstream.request_failed": "HTTP 502",
  "errors.upstream.rate_limited": "上游服务限流，请稍后重试。",
};

const INTERNAL_ERROR_PATTERNS = [
  /HTTPConnectionPool\(/i,
  /NewConnectionError/i,
  /Max retries exceeded/i,
  /Failed to establish a new connection/i,
  /Connection refused/i,
  /Connection aborted/i,
  /Connect timeout/i,
  /Read timed out/i,
  /Temporary failure in name resolution/i,
  /Name or service not known/i,
  /\bhost=.*\bport=\d+/i,
  /\b\d{1,3}(?:\.\d{1,3}){3}\b/,
  /https?:\/\/[^\s)]+/i,
  /\/v1\//i,
  /Traceback/i,
  /requests\./i,
  /urllib3\./i,
  /socket\./i,
  /Console API returned \d+/i,
];

function looksLikeInternalErrorMessage(message) {
  const text = String(message || "").trim();
  return Boolean(text) && INTERNAL_ERROR_PATTERNS.some((pattern) => pattern.test(text));
}

function extractRawErrorMessage(payload) {
  if (!payload) {
    return "";
  }
  if (typeof payload === "string") {
    return payload;
  }
  if (typeof payload.message === "string") {
    return payload.message;
  }
  if (typeof payload.error === "string") {
    return payload.error;
  }
  if (payload.error && typeof payload.error.message === "string") {
    return payload.error.message;
  }
  if (payload.error && typeof payload.error.key === "string") {
    return payload.error.key;
  }
  return "";
}

function extractErrorStatus(payload, fallbackStatus = 0) {
  for (const candidate of [
    payload?.error?.upstreamStatus,
    payload?.error?.upstream_status,
    payload?.upstreamStatus,
    payload?.upstream_status,
    payload?.error?.status,
    payload?.error?.status_code,
    payload?.status,
    payload?.status_code,
    fallbackStatus,
  ]) {
    const status = Number(candidate || 0);
    if (Number.isFinite(status) && status >= 400) {
      return status;
    }
  }
  return Number(fallbackStatus || 0);
}

function appendHttpStatus(message, status = 0) {
  const text = String(message || "").trim();
  const nextStatus = Number(status || 0);
  if (!nextStatus || !text || text.includes(`HTTP ${nextStatus}`)) {
    return text;
  }
  if (/^HTTP\s+\d+$/i.test(text)) {
    return `HTTP ${nextStatus}`;
  }
  return `${text}（HTTP ${nextStatus}）`;
}

function extractPublicErrorMessage(payload, status = 0) {
  const key = extractErrorKey(payload);
  const rawMessage = extractRawErrorMessage(payload) || key || "";
  const nextStatus = extractErrorStatus(payload, status);
  if (key && PUBLIC_ERROR_MESSAGES[key]) {
    return PUBLIC_ERROR_MESSAGES[key];
  }
  if (rawMessage && PUBLIC_ERROR_MESSAGES[rawMessage]) {
    return PUBLIC_ERROR_MESSAGES[rawMessage];
  }
  if (/Console API returned 400/i.test(rawMessage)) {
    return PUBLIC_ERROR_MESSAGES["errors.chat.stream_not_supported"] || "HTTP 502";
  }
  if (looksLikeInternalErrorMessage(rawMessage)) {
    return "HTTP 502";
  }
  if (nextStatus >= 500) {
    return `HTTP ${nextStatus}`;
  }
  return rawMessage || `HTTP ${nextStatus || 502}`;
}

function extractChatPublicErrorMessage(payload, status = 0) {
  return extractPublicErrorMessage(payload, status);
}

function extractChatErrorMessage(payload, status = 0) {
  const nextStatus = extractErrorStatus(payload, status);
  if (nextStatus === 500) {
    return "请求失败，请重试";
  }
  return appendHttpStatus(extractPublicErrorMessage(payload, nextStatus), nextStatus);
}

function extractErrorMessage(payload, status = 0) {
  return extractPublicErrorMessage(payload, status);
}

function delay(ms, signal) {
  return new Promise((resolve, reject) => {
    if (signal?.aborted) {
      reject(new DOMException("Aborted", "AbortError"));
      return;
    }
    const id = window.setTimeout(resolve, ms);
    signal?.addEventListener(
      "abort",
      () => {
        window.clearTimeout(id);
        reject(new DOMException("Aborted", "AbortError"));
      },
      { once: true },
    );
  });
}
